import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePageTitle } from "../../../../../contexts/PageTitleContext";
import { useError } from "../../../../../contexts/ErrorContext";
import getErrorMessage from "../../../../../utils/errorMessages";
import {
  fetchClientById,
  fetchClientAppointments,
  fetchClientStats,
  sendClientEmail,
  updateClient,
  deleteClient,
} from "../../../../../api/client";
import { exportToCsv } from "../../../../../utils/exportToCsv";
import styles from "./ClientDetail.module.scss";
import Button from "../../../../../components/Button/Button";
import InputCustom from "../../../../../components/InputCustom/InputCustom";
import ModalConfirmation from "../../../../../components/Modal/ModalConfirmation";
import { useFormik } from "formik";
import validationSchema from "../../../../../models/ClientSchema";
import { fetchAddressSuggestions } from "../../../../../api/address";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faEnvelope,
  faTags,
  faFileExport,
  faPencil,
  faTrashCan,
  faPhone,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import ClientKpiCards from "./ClientKpiCards/ClientKpiCards";
import ClientHistoryTable from "./ClientHistoryTable/ClientHistoryTable";
import ClientPromoPanel from "./ClientPromoPanel/ClientPromoPanel";
import AppointmentDetailModal from "./ClientHistoryTable/AppointmentDetailModal/AppointmentDetailModal";

function debounce(func, delay) {
  let timer;
  return function (...args) {
    const context = this;
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(context, args), delay);
  };
}

function getInitials(firstName, lastName) {
  const first = firstName?.[0] || "";
  const last = lastName?.[0] || "";
  return `${first}${last}`.toUpperCase();
}

function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const { errorMessage, setErrorMessage } = useError();

  const [client, setClient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState(null);
  const [topServices, setTopServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isEditing, setIsEditing] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [editErrorMessage, setEditErrorMessage] = useState("");
  const [editSuccessMessage, setEditSuccessMessage] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [quickEmailOpen, setQuickEmailOpen] = useState(false);
  const [quickEmailSubject, setQuickEmailSubject] = useState("");
  const [quickEmailMessage, setQuickEmailMessage] = useState("");
  const [quickEmailSending, setQuickEmailSending] = useState(false);
  const [quickEmailFeedback, setQuickEmailFeedback] = useState("");

  const [selectedAppointment, setSelectedAppointment] = useState(null);

  useEffect(() => {
    setPageTitle("Fiche client");
  }, [setPageTitle]);

  const loadClientData = useCallback(async () => {
    try {
      setLoading(true);

      const [clientData, appointmentsData, statsData] = await Promise.all([
        fetchClientById(id),
        fetchClientAppointments(id),
        fetchClientStats(id),
      ]);

      setClient(clientData);
      setAppointments(appointmentsData);
      setStats(statsData);
      setTopServices(statsData.topServices || []);
      setErrorMessage("");
    } catch (error) {
      const message = getErrorMessage(error.response?.status, "Fiche client");
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  }, [id, setErrorMessage]);

  useEffect(() => {
    loadClientData();
  }, [loadClientData]);

  // ------------------------------------------------------------
  // Mode édition inline (reprend la logique de EditClient)
  // ------------------------------------------------------------
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: client?.first_name || "",
      lastName: client?.last_name || "",
      email: client?.email || "",
      phoneNumber: client?.phone_number || "",
      address: client?.address || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const payload = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phoneNumber: values.phoneNumber || "",
          address: values.address || "",
        };

        const updatedClient = await updateClient(id, payload);
        setClient(updatedClient);
        setEditSuccessMessage("Client mis à jour avec succès.");
        setEditErrorMessage("");
        setIsEditing(false);
      } catch (error) {
        console.error("Erreur lors de la modification du client : ", error);
        setEditErrorMessage(
          error.response?.data?.message ||
            "Une erreur est survenue lors de la modification du client.",
        );
      }
    },
  });

  // Réinitialise le formulaire avec les valeurs actuelles du client
  // chaque fois qu'on entre en mode édition (formik ne le fait pas
  // automatiquement sans enableReinitialize couplé à un re-render clé).
  useEffect(() => {
    if (isEditing && client) {
      formik.setValues({
        firstName: client.first_name || "",
        lastName: client.last_name || "",
        email: client.email || "",
        phoneNumber: client.phone_number || "",
        address: client.address || "",
      });
      setEditErrorMessage("");
      setEditSuccessMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, client]);

  const fetchAddressSuggestionsDebouncedRef = useState(() =>
    debounce(async (inputText) => {
      try {
        const suggestions = await fetchAddressSuggestions(inputText);
        setAddressSuggestions(suggestions);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des suggestions d'adresse : ",
          error,
        );
      }
    }, 500),
  )[0];

  const selectAddress = (address) => {
    formik.setFieldValue("address", address);
    setAddressSuggestions([]);
  };

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setAddressSuggestions([]);
    setEditErrorMessage("");
    setEditSuccessMessage("");
  };

  // ------------------------------------------------------------
  // Suppression du client, avec confirmation puis retour à la liste
  // ------------------------------------------------------------
  const handleConfirmDelete = async () => {
    try {
      await deleteClient(id);
      setShowDeleteModal(false);
      navigate("/admin/clients");
    } catch (error) {
      console.error("Erreur lors de la suppression du client : ", error);
      setDeleteError(
        error.response?.data?.message ||
          "Une erreur est survenue lors de la suppression du client.",
      );
      setShowDeleteModal(false);
    }
  };

  // ------------------------------------------------------------
  // Export CSV / email rapide (inchangés)
  // ------------------------------------------------------------
  const handleExportCsv = () => {
    const rows = appointments.map((appointment) => ({
      Date: appointment.appointment_date
        ? new Date(appointment.appointment_date).toLocaleDateString("fr-FR")
        : "Non planifié",
      Prestations: (appointment.services || []).map((s) => s.name).join(", "),
      "Montant total": appointment.total_amount
        ? Number(appointment.total_amount).toFixed(2)
        : "0.00",
      Statut: appointment.status,
    }));

    exportToCsv(
      `historique_${client.last_name}_${client.first_name}.csv`,
      rows,
    );
  };

  const handleOpenQuickEmail = () => {
    setQuickEmailSubject("");
    setQuickEmailMessage("");
    setQuickEmailFeedback("");
    setQuickEmailOpen(true);
  };

  const handleSendQuickEmail = async () => {
    if (!quickEmailSubject.trim() || !quickEmailMessage.trim()) {
      setQuickEmailFeedback("Le sujet et le message sont requis.");
      return;
    }

    setQuickEmailSending(true);
    setQuickEmailFeedback("");

    try {
      await sendClientEmail(client.id, {
        subject: quickEmailSubject,
        message: quickEmailMessage,
      });
      setQuickEmailFeedback("Email envoyé avec succès.");
      setTimeout(() => setQuickEmailOpen(false), 1200);
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email :", error);
      setQuickEmailFeedback(
        error.response?.data?.message ||
          "Une erreur est survenue lors de l'envoi de l'email.",
      );
    } finally {
      setQuickEmailSending(false);
    }
  };

  const handleSelectAppointment = (appointment) => {
    setSelectedAppointment(appointment);
  };

  const handleCloseAppointmentModal = () => {
    setSelectedAppointment(null);
  };

  if (loading) {
    return <div className={styles.container}>Chargement...</div>;
  }

  if (!client) {
    return (
      <div className={styles.container}>
        {errorMessage && (
          <div className={styles.errorMessage}>{errorMessage}</div>
        )}
        <p>Client introuvable.</p>
        <Button
          color="var(--secondary-color)"
          onClick={() => navigate("/admin/clients")}
          style={{ padding: "7px 15px", fontSize: "0.85rem" }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Retour à la liste
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {errorMessage && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}
      {deleteError && <div className={styles.errorMessage}>{deleteError}</div>}
      <Button
        color="var(--secondary-color)"
        onClick={() => navigate("/admin/clients")}
        style={{
          padding: "5px 10px",
          fontSize: "0.8rem",
          marginBottom: "20px",
        }}
      >
        <FontAwesomeIcon icon={faArrowLeft} /> Retour à la liste
      </Button>
      {/* ---------------------------------------------------- */}
      {/* En-tête de fiche : affichage OU édition inline       */}
      {/* ---------------------------------------------------- */}
      {!isEditing ? (
        <div className={styles.headerCard}>
          <div className={styles.headerBanner}>
            <div className={styles.avatar}>
              {getInitials(client.first_name, client.last_name)}
            </div>

            <div className={styles.headerIdentity}>
              <h2 className={styles.clientName}>
                {client.first_name} {client.last_name}
              </h2>
              <p className={styles.clientSince}>
                Client depuis le{" "}
                {new Date(client.created_at).toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>

          <div className={styles.headerBody}>
            <div className={styles.contactGrid}>
              <div className={styles.contactItem}>
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className={styles.contactIcon}
                />
                <span>{client.email}</span>
              </div>
              {client.phone_number && (
                <div className={styles.contactItem}>
                  <FontAwesomeIcon
                    icon={faPhone}
                    className={styles.contactIcon}
                  />
                  <span>{client.phone_number}</span>
                </div>
              )}
              {client.address && (
                <div className={styles.contactItem}>
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    className={styles.contactIcon}
                  />
                  <span>{client.address}</span>
                </div>
              )}
            </div>

            <div className={styles.headerActions}>
              <Button
                color="var(--secondary-color)"
                onClick={handleOpenQuickEmail}
                style={{ padding: "8px 16px", fontSize: "0.85rem" }}
              >
                <FontAwesomeIcon icon={faEnvelope} /> Envoyer un email
              </Button>
              <Button
                color="var(--secondary-color)"
                onClick={() =>
                  document
                    .getElementById("promo-panel")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                style={{ padding: "8px 16px", fontSize: "0.85rem" }}
              >
                <FontAwesomeIcon icon={faTags} /> Offre personnalisée
              </Button>
              <Button
                color="var(--secondary-color)"
                onClick={handleExportCsv}
                style={{ padding: "8px 16px", fontSize: "0.85rem" }}
              >
                <FontAwesomeIcon icon={faFileExport} /> Export CSV
              </Button>

              <div className={styles.headerActionsDivider} />

              <button
                type="button"
                className={styles.iconButton}
                onClick={handleStartEdit}
                title="Modifier les informations"
              >
                <FontAwesomeIcon icon={faPencil} />
              </button>
              <button
                type="button"
                className={styles.iconButtonDanger}
                onClick={() => setShowDeleteModal(true)}
                title="Supprimer ce client"
              >
                <FontAwesomeIcon icon={faTrashCan} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.editSection}>
          <h2 className={styles.title}>Modifier les informations du client</h2>

          {editErrorMessage && (
            <div className={styles.errorMessage}>{editErrorMessage}</div>
          )}
          {editSuccessMessage && (
            <div className={styles.successMessage}>{editSuccessMessage}</div>
          )}

          <form onSubmit={formik.handleSubmit}>
            <div className={styles.row}>
              <div className={styles.fieldBlock}>
                <h3>Nom</h3>
                <InputCustom
                  id="lastName"
                  type="text"
                  name="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={styles.customInput}
                />
                {formik.touched.lastName && formik.errors.lastName ? (
                  <div className={styles.error}>{formik.errors.lastName}</div>
                ) : null}
              </div>

              <div className={styles.fieldBlock}>
                <h3>Prénom</h3>
                <InputCustom
                  id="firstName"
                  type="text"
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={styles.customInput}
                />
                {formik.touched.firstName && formik.errors.firstName ? (
                  <div className={styles.error}>{formik.errors.firstName}</div>
                ) : null}
              </div>
            </div>

            <div className={styles.fieldBlock}>
              <h3>Email</h3>
              <InputCustom
                id="email"
                type="text"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={styles.customInput}
              />
              {formik.touched.email && formik.errors.email ? (
                <div className={styles.error}>{formik.errors.email}</div>
              ) : null}
            </div>

            <div className={styles.fieldBlock}>
              <h3>Téléphone</h3>
              <InputCustom
                id="phoneNumber"
                type="tel"
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={styles.customInput}
              />
              {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
                <div className={styles.error}>{formik.errors.phoneNumber}</div>
              ) : null}
            </div>

            <div className={styles.fieldBlockAutocomplete}>
              <h3>Adresse</h3>
              <InputCustom
                id="address"
                type="text"
                name="address"
                value={formik.values.address}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  formik.handleChange(e);
                  if (inputValue.length >= 6) {
                    fetchAddressSuggestionsDebouncedRef(inputValue);
                  } else {
                    setAddressSuggestions([]);
                  }
                }}
                onBlur={formik.handleBlur}
                className={styles.customInput}
              />
              {formik.touched.address && formik.errors.address ? (
                <div className={styles.error}>{formik.errors.address}</div>
              ) : null}
              {addressSuggestions.length > 0 && (
                <div className={styles.dropdownMenu}>
                  {addressSuggestions.map((suggestion) => (
                    <div
                      key={suggestion.osm_id}
                      className={styles.dropdownItem}
                      onClick={() => selectAddress(suggestion.address)}
                    >
                      {suggestion.address}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.formActions}>
              <Button
                type="submit"
                color="var(--secondary-color)"
                style={{ padding: "7px 15px", fontSize: "0.85rem" }}
              >
                Enregistrer les modifications
              </Button>
              <Button
                type="button"
                color="var(--secondary-color)"
                onClick={handleCancelEdit}
                style={{ padding: "7px 15px", fontSize: "0.85rem" }}
              >
                Annuler
              </Button>
            </div>
          </form>
        </div>
      )}
      {/* ---------------------------------------------------- */}
      {/* Panneau email rapide                                  */}
      {/* ---------------------------------------------------- */}
      {quickEmailOpen && (
        <div className={styles.quickEmailPanel}>
          <h3>Envoyer un email à {client.first_name}</h3>
          <div className={styles.formField}>
            <label>Sujet</label>
            <input
              type="text"
              className={styles.input}
              value={quickEmailSubject}
              onChange={(e) => setQuickEmailSubject(e.target.value)}
            />
          </div>
          <div className={styles.formField}>
            <label>Message</label>
            <textarea
              className={styles.textarea}
              rows={6}
              value={quickEmailMessage}
              onChange={(e) => setQuickEmailMessage(e.target.value)}
            />
          </div>
          {quickEmailFeedback && (
            <div className={styles.feedbackMessage}>{quickEmailFeedback}</div>
          )}
          <div className={styles.formActions}>
            <Button
              color="var(--secondary-color)"
              onClick={handleSendQuickEmail}
              disabled={quickEmailSending}
              style={{ padding: "7px 15px", fontSize: "0.85rem" }}
            >
              {quickEmailSending ? "Envoi..." : "Envoyer"}
            </Button>
            <Button
              type="button"
              color="var(--secondary-color)"
              onClick={() => setQuickEmailOpen(false)}
              style={{ padding: "7px 15px", fontSize: "0.85rem" }}
            >
              Annuler
            </Button>
          </div>
        </div>
      )}
      {/* Section 1 : KPIs */}
      <ClientKpiCards stats={stats} />
      {/* Section 2 : Historique des prestations */}
      <div className={styles.sectionBlock}>
        <h3 className={styles.sectionTitle}>Historique des prestations</h3>
        <ClientHistoryTable
          appointments={appointments}
          onSelectAppointment={handleSelectAppointment}
        />
      </div>
      {/* Section 3 : Analyse / opportunités */}
      <div id="promo-panel">
        <ClientPromoPanel
          client={client}
          appointments={appointments}
          stats={stats}
          topServices={topServices}
        />
      </div>
      <ModalConfirmation
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        message={`Êtes-vous sûr de vouloir supprimer ${client.first_name} ${client.last_name} ? Cette action est irréversible.`}
        onConfirm={handleConfirmDelete}
      />
      <AppointmentDetailModal
        isOpen={!!selectedAppointment}
        onClose={handleCloseAppointmentModal}
        appointment={selectedAppointment}
      />
    </div>
  );
}

export default ClientDetail;
